import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram } from 'lucide-react';
import { siteData } from '../data/siteData';
import { Button } from './ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { href: '#inicio', label: 'Inicio' },
    { href: '#nosotros', label: 'Nosotros' },
    { href: '#servicios', label: 'Servicios' },
    { href: '#opiniones', label: 'Opiniones' },
    { href: '#contacto', label: 'Contacto' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        className={`w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'fixed top-0 left-0 bg-white/95 backdrop-blur-sm shadow-md'
            : 'absolute top-0 left-0 bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <a href="#inicio" className="flex-shrink-0" onClick={(e) => handleNavClick(e, '#inicio')}>
              <img
                src={siteData.business.logo}
                alt={siteData.business.name}
                className="h-12 sm:h-14 w-auto rounded-lg"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[var(--color-magenta)] transition-colors duration-200 rounded-lg hover:bg-gray-100"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={siteData.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 p-2 text-gray-700 hover:text-[var(--color-magenta)] transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Menú"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-white z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <img
              src={siteData.business.logo}
              alt={siteData.business.name}
              className="h-10 w-auto rounded-lg"
            />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Cerrar menú"
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col p-4 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:text-[var(--color-magenta)] hover:bg-gray-50 rounded-lg transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-auto p-4 border-t">
            <Button
              asChild
              className="w-full bg-[var(--color-magenta)] hover:bg-[var(--color-purple)] text-white"
            >
              <a href={siteData.hero.cta.url} target="_blank" rel="noopener noreferrer">
                {siteData.hero.cta.text}
              </a>
            </Button>
            <a
              href={siteData.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 py-2 text-gray-600 hover:text-[var(--color-magenta)] transition-colors"
            >
              <Instagram size={20} />
              <span>{siteData.social.instagramHandle}</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
